<template>
  <v-app class="theme--light">
    <!-- Background -->
    <div class="auth-page-premium">
      <div class="auth-bg-gradient"></div>

      <!-- Animated blobs -->
      <div class="auth-blob blob-1"></div>
      <div class="auth-blob blob-2"></div>

      <!-- Card -->
      <div class="auth-card-premium">
        <!-- Logo -->
        <div class="mb-5 d-flex justify-center">
          <v-img src="/LogoSvg.svg" contain height="70" max-width="220"></v-img>
        </div>
        <div class="auth-title-premium mt-2">Welcome Back</div>
        <div class="auth-sub-premium mt-2">
          Society Portal · Society Management
        </div>

        <!-- Form -->
        <v-form>
          <!-- Country + Mobile -->
          <div class="auth-field-row">
            <country-code-select
              v-model="selectedCountryCode"
              @onSelect="onSelect"
              class="custom-country-code-select"
            />
            <v-text-field
              v-model="user.mobileNo"
              name="mobileNo"
              data-vv-as="Mobile Number"
              label="Mobile Number"
              outlined
              dense
              type="text"
              v-validate="'required|integer|min:6|max:15'"
              :error="
                errors.first('mobileNo') != null ||
                serverSideErrors.mobileNo != null
              "
              :error-messages="
                errors.first('mobileNo') || serverSideErrors.mobileNo
              "
              hide-details="auto"
              class="auth-field-input rounded-lg"
            />
          </div>
        </v-form>

        <!-- Send OTP Button -->
        <v-btn
          block
          depressed
          large
          :loading="loading"
          @click="login"
          class="auth-btn mt-4 rounded-lg"
          :style="`background: linear-gradient(135deg, ${themeColor}, ${darkenColor(
            themeColor,
          )}) !important; color:#fff !important; font-size:14px; font-weight:700; letter-spacing:0; text-transform:none; height:48px;`"
        >
          Send OTP
          <v-icon right style="font-size: 16px">mdi-arrow-right</v-icon>
        </v-btn>

        <!-- Demo note -->
        <div class="auth-hint-box mt-4">
          <v-icon small :color="themeColor">mdi-information-outline</v-icon>
          <span>Enter your registered mobile number to receive an OTP</span>
        </div>
      </div>
    </div>

    <Alert />
  </v-app>
</template>

<script>
import Alert from "../../Alert.vue";
import "@/assets/style.css";
import { mapActions, mapMutations, mapState } from "vuex";

export default {
  components: { Alert },

  data() {
    return {
      user: { mobileNo: "", country_code: "" },
      selectedCountryCode: "",
      loading: false,
      serverSideErrors: {},
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "savedPrimaryColor"]),
    themeColor() {
      return this.savedPrimaryColor || "#f87676";
    },
  },

  created() {
    this.resetTheme();
    window.__societyVuetify = this.$vuetify;
    this.forceLightMode({ vuetify: this.$vuetify });
  },


  methods: {
    ...mapActions("theme", ["applyAppearance", "initThemeListener", "forceLightMode"]),
    ...mapMutations("alert", ["setAlert"]),
    ...mapMutations("theme", ["resetTheme"]),

    onSelect(event) {
      this.user.country_code = "+" + event.dialCode;
    },

    darkenColor(hex) {
      if (!hex || !hex.startsWith("#")) return "#cc5555";
      try {
        let r = parseInt(hex.slice(1, 3), 16);
        let g = parseInt(hex.slice(3, 5), 16);
        let b = parseInt(hex.slice(5, 7), 16);
        return `#${Math.max(0, r - 30)
          .toString(16)
          .padStart(2, "0")}${Math.max(0, g - 30)
          .toString(16)
          .padStart(2, "0")}${Math.max(0, b - 30)
          .toString(16)
          .padStart(2, "0")}`;
      } catch {
        return hex;
      }
    },

    login() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.loading = true;
          this.$axios
            .post("/societyadmin/sendOtp", this.user)
            .then((response) => {
              const data = response.data;
              this.loading = false;
              if (data.status) {
                this.setAlert({
                  showAlert: true,
                  variant: "green accent-4",
                  timeout: "2000",
                  message: data.message,
                });
                this.$router.push({
                  path: "/societyadmin/verifyOtp/" + this.user.mobileNo,
                });
                localStorage.setItem("otp_country_code", this.user.country_code || "");
              } else {
                if (data.errors) {
                  this.serverSideErrors = {};
                  for (let i = 0; i < data.errors.length; i++) {
                    this.serverSideErrors[data.errors[i].field] =
                      data.errors[i].message;
                  }
                } else {
                  this.setAlert({
                    showAlert: true,
                    variant: "red accent-3",
                    timeout: "2000",
                    message: data.message,
                  });
                }
              }
            });
        }
      });
    },
  },

  mounted() {
    const mobileNo = localStorage.getItem("mobileNo");
    if (mobileNo) this.user.mobileNo = mobileNo;
  },
};
</script>

<style scoped>
/* ══ Page shell ══ */
.auth-page-premium {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: url("/security.svg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
}

.auth-bg-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(15, 23, 41, 0.7) 0%,
    rgba(15, 23, 41, 0.4) 100%
  );
  backdrop-filter: blur(2px);
  pointer-events: none;
}

/* Blobs - adjusted for better aesthetics with security.svg */
.auth-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.2;
  animation: blobFloat 12s ease-in-out infinite alternate;
}
.blob-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, var(--brand, #10b981), transparent);
  top: -150px;
  left: -150px;
}
.blob-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #3b82f6, transparent);
  bottom: -100px;
  right: -100px;
  animation-delay: 3s;
}
@keyframes blobFloat {
  from {
    transform: translate(0, 0) scale(1);
  }
  to {
    transform: translate(20px, 20px) scale(1.08);
  }
}

/* ══ Card ══ */
.auth-card-premium {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  padding: 48px 40px;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  text-align: center;
  position: relative;
  z-index: 10;
  animation: cardIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@media (max-width: 480px) {
  .auth-card-premium {
    padding: 32px 20px;
    width: 94%;
    border-radius: 18px;
  }
}
@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(24px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* ══ Logo ══ */
.auth-logo-wrap {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  margin: 0 auto 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(248, 118, 118, 0.35);
}

/* ══ Title / Sub ══ */
.auth-title-premium {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.04em;
  color: var(--tx, #111827);
  margin-bottom: 6px;
}
.auth-sub-premium {
  font-size: 13px;
  color: var(--tx3, #6b7280);
  margin-bottom: 28px;
}

/* ══ Field row ══ */
.auth-field-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  text-align: left;
  width: 100%;
}

.auth-field-input {
  margin: 0 !important;
  padding: 0 !important;
  flex: 1;
  min-width: 0;
}

/* Ensure Vuetify error message wraps correctly */
.auth-field-input >>> .v-text-field__details {
  white-space: normal !important;
  word-break: break-word !important;
}

.custom-country-code-select {
  width: 76px;
  min-width: 76px;
  flex-shrink: 0;
}

@media (max-width: 480px) {
  .auth-field-row {
    gap: 6px;
  }
  .custom-country-code-select {
    width: 68px;
    min-width: 68px;
  }
}

/* ══ Hint box ══ */
.auth-hint-box {
  background: var(--surface2, #f9fafb);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 8px;
  padding: 11px 14px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: var(--tx3, #6b7280);
  text-align: left;
  word-break: break-word;
}

/* ══ Theme toggle pill ══ */
.theme-toggle-pill {
  width: 56px;
  height: 30px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 15px;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 3px 4px;
  transition: all 0.25s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.theme-toggle-pill.dark-mode {
  background: #162035;
  border-color: rgba(255, 255, 255, 0.12);
}
.theme-toggle-thumb {
  width: 22px;
  height: 22px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.theme-toggle-pill.dark-mode .theme-toggle-thumb {
  transform: translateX(26px);
  background: #253350;
}
</style>
